import React, { useState, useEffect, useCallback } from "react";
import {
  registerAdmin,
  registerManager,
  registerClient,
  registerPlatformAdmin,
  updateUser,
  getAllCompanies,
  getUserDetails,
  addUserToOrg,
  updateMemberRole,
  listOrgRoles,
  removeUserFromCompany,
} from "../api";
import { useNotification } from "../contexts/NotificationContext";
import { useActiveRole } from "../hooks/useActiveRole";

// Friendly labels for role names. The backend stores snake_case identifiers
// (org_viewer, org_admin…) that mean nothing to a customer. Map them to plain
// English so the membership table reads naturally. Unknown roles fall back to
// a title-cased version of the raw name. (Mirrors CompanyDetailModal.)
const ROLE_LABELS = {
  platform_admin: "Platform Admin",
  org_owner: "Owner",
  org_admin: "Admin",
  org_analyst: "Analyst",
  org_viewer: "Viewer",
  org_scoped: "Scoped",
};
const roleLabel = (name) => {
  if (!name) return "—";
  if (ROLE_LABELS[name]) return ROLE_LABELS[name];
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const AddEditUserModal = ({ open, onClose, onSuccess, user = null }) => {
  const isEdit = !!user;
  // Multi-Org Membership — Epic 1 deferred this read to Epic 2. Role is per
  // organization; the active-org role drives what an org_admin can create, but
  // platform_admin is a USER-level trait (sourced from the primary membership)
  // so a platform_admin keeps the full role picker regardless of which org
  // they're currently viewing.
  const { isPlatformAdmin, role: activeRole } = useActiveRole();
  const currentUserRole = isPlatformAdmin ? 0 : (activeRole ?? 3);
  const { showSuccess, showError, showConfirm } = useNotification();
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "3",
    // profiles.status is an enum on the Supabase side: active | suspended |
    // deleted | pending_invite. `fn_admin_update_user_profile` enforces it.
    status: "active",
    company_id: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  // 2026-07-27 — every new user MUST be assigned to an org, otherwise
  // `fn_admin_list_users` never returns them and they show up as orphans.
  // (Discovered when ccss_sio@yahoo.com.ph was created without an org and
  // vanished from the list.) Super Admin (role=0) is the only role allowed
  // to skip the assignment — they operate cross-org.
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setOrgsLoading(true);
    getAllCompanies()
      .then((resp) => { if (!cancelled) setOrgs(resp?.data || []); })
      .catch(() => { if (!cancelled) setOrgs([]); })
      .finally(() => { if (!cancelled) setOrgsLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Determine available roles based on current user's role.
  // Super Admin (role=0) is offered whenever the editor is themselves a Super
  // Admin — both when adding a new user and when editing an existing one — so
  // they can create/promote/preserve other Super Admins.
  const getAvailableRoles = () => {
    if (currentUserRole === 0) {
      return [
        { value: "0", label: "Super Admin" },
        { value: "1", label: "Admin" },
        { value: "2", label: "Manager" },
        { value: "3", label: "Client" },
      ];
    } else if (currentUserRole === 1) {
      return [
        { value: "1", label: "Admin" },
        { value: "2", label: "Manager" },
        { value: "3", label: "Client" },
      ];
    } else if (currentUserRole === 2) {
      return [{ value: "3", label: "Client" }];
    }
    return [{ value: "3", label: "Client" }];
  };

  const availableRoles = getAvailableRoles();

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || "",
        password: "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        role: String(user.role ?? "3"),
        // Loaded user.status can be the enum string OR the legacy 0/1
        // (shapeAdminUserRow maps 'active' → 1). Normalize back to enum.
        status: (
          user.status === "active" || user.status === 1 || user.status === "1"
            ? "active"
            : (typeof user.status === "string" ? user.status : "suspended")
        ),
        company_id: user.company_id ? String(user.company_id) : "",
      });
    } else {
      setFormData({
        email: "",
        password: "",
        first_name: "",
        last_name: "",
        role: "3",
        status: "active",
        company_id: "",
      });
    }
    setError("");
  }, [user, open]);

  // ── Multi-Org Membership — Epic 2: per-org memberships table ─────────────
  // Role is per organization, not per user. In edit mode a platform_admin sees
  // every org the user belongs to (getUserDetails → fn_admin_get_user returns
  // `memberships`), each with its own role dropdown (updateMemberRole, role ID)
  // and a Remove action (removeUserFromCompany). "+ Add to organization" picks
  // an org the user isn't in and assigns a role (addUserToOrg, role NAME).
  // org_admin continues to use the per-org member dialog (CompanyDetailModal);
  // this surface is platform_admin-only because cross-user getUserDetails goes
  // through fn_admin_get_user, which is platform-admin-gated. (Risks/notes §2.)
  const [memberships, setMemberships] = useState([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [membershipsError, setMembershipsError] = useState("");
  const [orgRoles, setOrgRoles] = useState([]); // system roles minus platform_admin
  const [membershipBusy, setMembershipBusy] = useState({}); // { [orgId]: bool }
  const [addOrgId, setAddOrgId] = useState("");
  const [addRoleName, setAddRoleName] = useState("org_viewer");
  const [addingToOrg, setAddingToOrg] = useState(false);

  const loadMemberships = useCallback(async (userId) => {
    if (!userId) return;
    setMembershipsLoading(true);
    setMembershipsError("");
    try {
      const resp = await getUserDetails(userId);
      const m = Array.isArray(resp?.data?.memberships) ? resp.data.memberships : [];
      // Only active memberships are editable; dropped/removed rows would be
      // misleading in a table whose actions are "change role / remove".
      setMemberships(m.filter((x) => !x.status || x.status === "active"));
    } catch (e) {
      setMembershipsError(e?.message || "Failed to load memberships");
      setMemberships([]);
    } finally {
      setMembershipsLoading(false);
    }
  }, []);

  // Fetch memberships + org roles when the edit modal opens (platform_admin only).
  useEffect(() => {
    if (!open || !isEdit || !isPlatformAdmin || !user?.id) return;
    let cancelled = false;
    (async () => {
      await loadMemberships(user.id);
      if (cancelled) return;
      try {
        const r = await listOrgRoles();
        if (!cancelled) setOrgRoles((Array.isArray(r?.data) ? r.data : []).filter((x) => x.name !== "platform_admin"));
      } catch {
        if (!cancelled) setOrgRoles([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isEdit, isPlatformAdmin, user?.id, loadMemberships]);

  const orgNameFor = (m) => {
    const o = orgs.find((x) => String(x.id) === String(m.org_id));
    return o?.name || o?.slug || m.org_name || m.org_public_id || m.org_id;
  };

  const handleMembershipRoleChange = async (orgId, roleId) => {
    setMembershipBusy((b) => ({ ...b, [orgId]: true }));
    // Optimistic update; rollback by refetching the source of truth on error.
    setMemberships((ms) => ms.map((m) => (String(m.org_id) === String(orgId)
      ? { ...m, role_id: roleId, role_name: orgRoles.find((r) => r.id === roleId)?.name || m.role_name }
      : m)));
    try {
      await updateMemberRole(orgId, user.id, roleId);
      showSuccess("Role updated", "The user's role in that organization has been updated.");
      onSuccess?.();
    } catch (e) {
      await loadMemberships(user.id);
      showError("Role update failed", e?.message || "Failed to update role.");
    } finally {
      setMembershipBusy((b) => ({ ...b, [orgId]: false }));
    }
  };

  const handleRemoveMembership = async (m) => {
    const isLast = memberships.length <= 1;
    const message = isLast
      ? `Remove ${user.first_name || user.display_name || user.email} from ${orgNameFor(m)}? This is their last organization — the user will still exist and can sign in, but will see nothing until they're added to another org.`
      : `Remove ${user.first_name || user.display_name || user.email} from ${orgNameFor(m)}? They will still be a member of ${memberships.length - 1} other org${memberships.length - 1 === 1 ? "" : "s"}.`;
    const confirmed = await showConfirm({
      title: "Remove from organization",
      message,
      confirmText: "Remove",
      cancelText: "Cancel",
      confirmButtonClass: "bg-red-500 hover:bg-red-600",
    });
    if (!confirmed) return;
    try {
      await removeUserFromCompany(m.org_id, user.id);
      showSuccess("Removed", `User removed from ${orgNameFor(m)}.`);
      await loadMemberships(user.id);
      onSuccess?.();
    } catch (e) {
      showError("Remove failed", e?.message || "Failed to remove membership.");
    }
  };

  const handleAddToOrg = async () => {
    if (!addOrgId) { setError("Pick an organization to add."); return; }
    setAddingToOrg(true);
    setError("");
    try {
      await addUserToOrg(user.id, addOrgId, addRoleName);
      showSuccess("Added", `User added to ${orgs.find((o) => String(o.id) === String(addOrgId))?.name || "organization"}.`);
      setAddOrgId("");
      setAddRoleName("org_viewer");
      await loadMemberships(user.id);
      onSuccess?.();
    } catch (e) {
      setError(e?.message || "Failed to add user to organization.");
    } finally {
      setAddingToOrg(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Generate a strong password
  const generateStrongPassword = () => {
    const length = 16;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const allChars = uppercase + lowercase + numbers + symbols;
    
    let password = '';
    // Ensure at least one of each type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setFormData((prev) => ({ ...prev, password }));
    setShowPassword(true);
  };

  // Copy password to clipboard
  const copyPasswordToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(formData.password);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const role = parseInt(formData.role);

      // Enforce org assignment for every non-super-admin. Super Admins operate
      // cross-org and don't need a membership; everyone else must be linked to
      // an org or fn_admin_list_users won't return them (they become invisible).
      if (!isEdit && role !== 0 && !formData.company_id) {
        setError("Please assign the user to an organization.");
        setLoading(false);
        return;
      }

      if (isEdit) {
        // PATCH semantics: diff every editable field against the loaded user
        // and only include the ones that actually changed. Backend's
        // fn_admin_update_user_profile accepts partial p_updates so unchanged
        // columns are left untouched. Empty diff = no-op (no request at all).
        //
        // Why this matters beyond bandwidth: sending status/email/etc.
        // unconditionally forces the backend to revalidate every field on
        // every save. That's how the "Invalid status: 1" and the doomed
        // admin-user-ops email-update calls surface on trivial edits.
        const updateData = {};

        // Trim-and-compare for text; direct compare for enums/booleans.
        const changed = (a, b) => (a || '').trim() !== (b || '').trim();

        if (changed(formData.first_name, user?.first_name)) {
          updateData.first_name = formData.first_name;
        }
        if (changed(formData.last_name, user?.last_name)) {
          updateData.last_name = formData.last_name;
        }

        // Normalize loaded user.status back to the enum string so we can
        // compare like-for-like (shapeAdminUserRow maps 'active' → 1).
        const originalStatus = (
          user?.status === 'active' || user?.status === 1 || user?.status === '1'
            ? 'active'
            : (typeof user?.status === 'string' ? user.status : 'suspended')
        );
        if (formData.status !== originalStatus) {
          updateData.status = formData.status;
        }

        const originalEmail = (user?.email || '').trim().toLowerCase();
        const nextEmail = (formData.email || '').trim().toLowerCase();
        if (nextEmail && nextEmail !== originalEmail) {
          updateData.email = formData.email;
        }
        if (formData.password) {
          updateData.password = formData.password;
        }

        if (Object.keys(updateData).length === 0) {
          setError("No changes to save.");
          setLoading(false);
          return;
        }

        await updateUser(user.id, updateData);
      } else {
        // Create new user
        const userData = {
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          status: formData.status,
          org_id: formData.company_id || null,
          company_id: formData.company_id || null, // registerX() accepts either key
        };

        if (role === 0) {
          await registerPlatformAdmin(userData);
        } else if (role === 1) {
          await registerAdmin(userData);
        } else if (role === 2) {
          await registerManager(userData);
        } else if (role === 3) {
          await registerClient(userData);
        }
      }

      await onSuccess();
      onClose();
    } catch (err) {
      setError(err?.message || `Failed to ${isEdit ? "update" : "create"} user`);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b dark:border-gray-800">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {isEdit ? "Edit User" : "Add New User"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Username */}
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
              />
            </div>

            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                First Name
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
              />
            </div>

            {/* Password */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password {!isEdit && "*"}
              </label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!isEdit}
                    placeholder={isEdit ? "Leave blank to keep current" : ""}
                    className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 pr-20 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                    {/* Toggle Password Visibility */}
                    {formData.password && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    )}
                    {/* Copy Password */}
                    {formData.password && (
                      <button
                        type="button"
                        onClick={copyPasswordToClipboard}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                        title="Copy password"
                      >
                        {passwordCopied ? (
                          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                {/* Generate Password Button */}
                <button
                  type="button"
                  onClick={generateStrongPassword}
                  className="px-4 py-2 rounded-lg border-2 border-[#32B1FF] text-[#32B1FF] hover:bg-[#32B1FF] hover:text-white transition-all font-medium text-sm flex items-center gap-2 whitespace-nowrap"
                  title="Generate a strong random password"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Generate
                </button>
              </div>
              {!isEdit && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Minimum 6 characters. Click "Generate" for a strong password (16 chars with uppercase, lowercase, numbers, and symbols)
                </p>
              )}
            </div>

            {/* Role — create only. 2026-08-08 (backend brief §2): a role is a
                property of an org MEMBERSHIP (organization_members), not of the
                user, so Edit User has no org context to express it in — and its
                old "Client" option mapped to no real role anyway, so the field
                silently wrote nothing. Edit keeps identity only (name, email,
                password, status); role changes live in the org dialog's member
                list via fn_update_member_role. On create the picker still
                selects which register path (and initial membership role) to use. */}
            {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Role *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] disabled:opacity-50"
              >
                {availableRoles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {currentUserRole === 2 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Managers can only create client accounts</p>
              )}
            </div>
            )}

            {/* Organization — required for every role except Super Admin, who
                operates cross-org. Skipping this on create makes the user
                invisible to fn_admin_list_users. */}
            {!isEdit && parseInt(formData.role) !== 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Organization *
                </label>
                <select
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleChange}
                  required
                  disabled={orgsLoading}
                  className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] disabled:opacity-50"
                >
                  <option value="">{orgsLoading ? "Loading organizations…" : "Select an organization"}</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name || o.slug || o.id}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Every user must be linked to an org so they appear in the user list. Change later from the org's settings page.
                </p>
              </div>
            )}

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="pending_invite">Pending Invite</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>
          </div>

          {/* Multi-Org Membership — Epic 2: per-org memberships table
              (platform_admin, edit mode). Replaces the single org/role pair
              with one row per org, each with its own role + Remove, plus an
              "+ Add to organization" picker. Create mode and non-platform-admin
              edit keep the legacy identity-only form above. */}
          {isEdit && isPlatformAdmin && (
            <div className="border-t dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Organizations
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Role is per organization
                </span>
              </div>

              {membershipsLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading memberships…</p>
              ) : membershipsError ? (
                <p className="text-sm text-red-600 dark:text-red-400">{membershipsError}</p>
              ) : (
                <div className="space-y-2">
                  {memberships.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      This user is not in any organization. They can sign in but will see nothing.
                    </p>
                  )}
                  {memberships.map((m) => (
                    <div
                      key={m.org_id}
                      className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {orgNameFor(m)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">
                          {m.status || "active"}
                        </p>
                      </div>
                      <select
                        value={m.role_id || ""}
                        disabled={!!membershipBusy[m.org_id]}
                        onChange={(e) => handleMembershipRoleChange(m.org_id, e.target.value)}
                        className="text-xs border dark:border-gray-700 rounded-md px-2 py-1.5 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] disabled:opacity-50"
                        title="Change role in this organization"
                      >
                        {!orgRoles.some((r) => r.id === m.role_id) && (
                          <option value={m.role_id || ""} disabled>
                            {roleLabel(m.role_name)}
                          </option>
                        )}
                        {orgRoles.map((r) => (
                          <option key={r.id} value={r.id}>{roleLabel(r.name)}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => handleRemoveMembership(m)}
                        disabled={!!membershipBusy[m.org_id]}
                        className="text-xs px-2 py-1.5 rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                        title="Remove from this organization"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {/* + Add to organization */}
                  <div className="flex flex-wrap items-end gap-2 pt-2 border-t dark:border-gray-700">
                    <div className="flex-1 min-w-[160px]">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Add to organization
                      </label>
                      <select
                        value={addOrgId}
                        onChange={(e) => setAddOrgId(e.target.value)}
                        disabled={orgsLoading || addingToOrg}
                        className="w-full border dark:border-gray-700 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] disabled:opacity-50"
                      >
                        <option value="">{orgsLoading ? "Loading…" : "Select an organization"}</option>
                        {orgs
                          .filter((o) => !memberships.some((mm) => String(mm.org_id) === String(o.id)))
                          .map((o) => (
                            <option key={o.id} value={o.id}>{o.name || o.slug || o.id}</option>
                          ))}
                      </select>
                    </div>
                    <div className="min-w-[140px]">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Role
                      </label>
                      <select
                        value={addRoleName}
                        onChange={(e) => setAddRoleName(e.target.value)}
                        disabled={addingToOrg || orgRoles.length === 0}
                        className="w-full border dark:border-gray-700 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] disabled:opacity-50"
                      >
                        {orgRoles.length === 0 && <option value="org_viewer">Viewer</option>}
                        {orgRoles.map((r) => (
                          <option key={r.id} value={r.name}>{roleLabel(r.name)}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddToOrg}
                      disabled={addingToOrg || !addOrgId}
                      className="px-3 py-1.5 text-sm rounded-md bg-[#32B1FF] text-white hover:bg-[#259ae6] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addingToOrg ? "Adding…" : "Add"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Chat and Voice product entitlement is managed per-client on the
              Clients page (writes clients.ai_chatbot_enabled /
              ai_phone_agent_enabled). Removed from the user modal 2026-07-27 to
              keep the two concerns separate. */}

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-[#32B1FF] text-white rounded-lg hover:bg-[#259ae6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Saving..." : isEdit ? "Update User" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditUserModal;

